import type { ClassView, LearnerClient } from "@tutor/live";
import type { ClassChapterOption } from "@tutor/protocol";
import { useId } from "react";
import { useLocale, useT, type MessageKey } from "../i18n";

// « En classe en ce moment » (ADR-0053) on the start of a session: the child says what the
// class is on, from the fixed list for their classe, in one pick. The engine then puts that
// chapter first. Hidden until a parent has given the classe (no list to pick from).

const SUBJECT_KEYS: Readonly<Record<string, MessageKey>> = {
  math: "class.subject.math",
  fra: "class.subject.fra",
  sci: "class.subject.sci",
  svt: "class.subject.svt",
  pc: "class.subject.pc",
  hg: "class.subject.hg",
  emc: "class.subject.emc",
  tech: "class.subject.tech",
};

function bySubject(chapters: readonly ClassChapterOption[]): [string, ClassChapterOption[]][] {
  const groups = new Map<string, ClassChapterOption[]>();
  for (const c of chapters) groups.set(c.subject, [...(groups.get(c.subject) ?? []), c]);
  return [...groups];
}

export function ClassPicker(props: { classView: ClassView | null; client: LearnerClient }) {
  const { classView, client } = props;
  const t = useT();
  const locale = useLocale();
  const labelId = useId();
  if (classView?.classNow == null || classView.chapters.length === 0) return null;
  const section = classView.classNow.section ?? null;
  const testOn = section === null ? null : (classView.classNow.testOn ?? null);
  const subjectLabel = (code: string) => {
    const key = SUBJECT_KEYS[code];
    return key === undefined ? code : t(key);
  };
  return (
    <div className="class-picker">
      <label id={labelId} htmlFor={`${labelId}-select`} className="class-picker-label">
        {t(section === null ? "class.ask" : "class.now")}
      </label>
      <select
        id={`${labelId}-select`}
        className="class-picker-select"
        value={section ?? ""}
        onChange={(e) => {
          client.pickChapter(e.target.value === "" ? null : e.target.value);
        }}
      >
        <option value="">{t("class.none")}</option>
        {bySubject(classView.chapters).map(([subject, list]) => (
          <optgroup key={subject} label={subjectLabel(subject)}>
            {list.map((c) => (
              <option key={c.code} value={c.code}>
                {c.topic}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {testOn !== null && (
        <p className="class-picker-test">
          {t("class.test", {
            date: new Date(`${testOn}T12:00:00Z`).toLocaleDateString(locale, {
              weekday: "long",
              day: "numeric",
              month: "long",
              timeZone: "UTC",
            }),
          })}
        </p>
      )}
    </div>
  );
}
